import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Award,
  Clock,
  FileQuestion,
  ArrowRight,
  Loader2,
  ChevronLeft,
  Lock,
  Trophy,
  Star,
  Sparkles,
  Play,
  RotateCcw,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useStudent } from "../../context/StudentContext";
import { api, ApiError, type CocExam, type CocScoreResponse } from "../../lib/api";
import { MathText } from "../../components/MathText";
import { QuestionFigure } from "../../components/QuestionFigure";
import { QuestionPassageLayout } from "../../components/student/QuestionPassageLayout";
import { UpgradeCard } from "../../components/student/UpgradeCard";
import { PremiumGate } from "../../components/ui/PremiumGate";
import { Button } from "../../components/ui/Button";
import { SkeletonCard } from "../../components/ui/Skeleton";
import { StatusMessage, mapApiError, type MappedApiError } from "../../components/ui/StatusMessage";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { toast } from "../../components/ui/Toast";
import { saveRedirectTarget } from "../../lib/redirect";
import { formatDisplayTitle } from "../../lib/formatTitle";

export function CocExamsPage() {
  const { tokens } = useAuth();
  const { openUpgrade, isPremium } = useStudent();
  const token = tokens?.accessToken ?? null;
  const navigate = useNavigate();
  const location = useLocation();

  const [exams, setExams] = useState<CocExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<MappedApiError | null>(null);

  const fetchExams = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    api.student.coc.list(token)
      .then((cocList) => {
        setExams(cocList);
        setError(null);
      })
      .catch((e) => {
        const mapped = mapApiError(e);
        if (mapped.isSessionExpired) {
          saveRedirectTarget(location.pathname);
          navigate(`/auth/login?redirect=${encodeURIComponent(location.pathname)}`);
          return;
        }
        setError(mapped);
      })
      .finally(() => setLoading(false));
  }, [token, location.pathname, navigate]);

  useEffect(() => {
    fetchExams();
  }, [fetchExams]);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-bold text-primary uppercase tracking-wider mb-1">
            <Award size={16} /> Certificate of competency
          </div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-foreground">COC practice exams</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5">
            You take the COC exam after you finish the semester.
          </p>
        </div>

      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" aria-busy="true" aria-label="Loading exams">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} lines={3} className="h-44" />
          ))}
        </div>
      ) : error ? (
        <StatusMessage
          variant={error.variant}
          title={error.title}
          message={error.message}
          requestId={error.requestId}
          action={{
            label: "Retry",
            onClick: fetchExams,
          }}
        />
      ) : exams.length === 0 ? (
        <div className="text-center py-16 bg-card rounded-lg border border-dashed border-border p-8">
          <Award size={36} className="mx-auto text-muted-foreground/50 mb-3" />
          <h3 className="font-bold text-base">No COC exams available</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto mt-1">
            COC exams for your purchased semester and stream will appear here.
          </p>
        </div>
      ) : (
        <>
        {(() => {
          const lockedOf = (exam: CocExam) => exam.access === "locked" || (!isPremium && !exam.is_free && exam.access !== "preview");
          const grid = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
          const renderExam = (exam: CocExam) => {
            const isLocked = lockedOf(exam);
            const isPreview = exam.access === "preview" || exam.is_preview;

            return (
                <div
                  key={exam.id}
                  className={`bg-card rounded-lg border border-border p-5 flex flex-col justify-between space-y-4 hover:border-primary/40 transition ${
                    isLocked ? "opacity-70" : ""
                  }`}
                >
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="px-2.5 py-0.5 rounded-xs text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
                        {exam.category}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {isLocked ? (
                          <span className="text-xs font-bold text-warning bg-warning/10 border border-warning/30 px-2 py-0.5 rounded-xs flex items-center gap-1">
                            <Lock size={10} /> Locked
                          </span>
                        ) : isPreview ? (
                          <span className="text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-xs flex items-center gap-1">
                            Preview
                          </span>
                        ) : null}
                        {exam.year && (
                          <span className="text-xs font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-xs">
                            {exam.year}
                          </span>
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-sm sm:text-base text-foreground line-clamp-2">
                        {formatDisplayTitle(exam.title)}
                      </h3>
                      {exam.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{exam.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-border flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <FileQuestion size={13} className="text-primary" />
                        {exam.question_count} questions
                      </span>
                      {exam.time_limit_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock size={13} />
                          {exam.time_limit_minutes}m
                        </span>
                      )}
                    </div>

                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={isLocked}
                      onClick={() => navigate(`/student/coc/${exam.id}`)}
                      icon={<ArrowRight size={12} />}
                      iconPosition="right"
                    >
                      {isLocked ? "Premium" : isPreview ? "Start preview" : "Take exam"}
                    </Button>
                  </div>
                </div>
            );
          };
          const open = exams.filter((e) => !lockedOf(e));
          const locked = exams.filter(lockedOf);
          return (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-1 duration-300">
              {open.length > 0 && <div className={grid}>{open.map(renderExam)}</div>}
              {locked.length > 0 && (
                <div className="relative min-h-52">
                  <div aria-hidden="true" inert className={`${grid} pointer-events-none select-none blur-sm`}>{locked.map(renderExam)}</div>
                  <div className="absolute inset-0 flex justify-center p-3">
                    <div className="sticky top-28 w-full max-w-sm self-start">
                      <UpgradeCard variant="stack" title="Premium" message="Preview the free questions first, then unlock the remaining COC exams together." />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        </>
      )}
    </div>
  );
}

export function CocExamPlayPage() {
  const { cocId } = useParams<{ cocId: string }>();
  const { tokens } = useAuth();
  const { openUpgrade, isPremium } = useStudent();
  const token = tokens?.accessToken ?? null;
  const navigate = useNavigate();
  const location = useLocation();

  const [exam, setExam] = useState<CocExam | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<MappedApiError | null>(null);
  const [showExitConfirm, setShowExitConfirm] = useState(false);

  // Mode: "entry" -> "playing" -> "results"
  const [mode, setMode] = useState<"entry" | "playing" | "results">("entry");
  const [isTimed, setIsTimed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [serverScore, setServerScore] = useState<CocScoreResponse | null>(null);

  const loadExam = useCallback(() => {
    if (!token || !cocId) return;
    setLoading(true);
    setError(null);
    api.student.coc.get(token, cocId)
      .then((data) => {
        setExam(data);
        const limitMinutes = data.time_limit_minutes || 60;
        setTimeLeft(limitMinutes * 60);
        setError(null);
      })
      .catch((e) => {
        const mapped = mapApiError(e);
        if (mapped.isPremiumRequired) {
          openUpgrade();
        }
        if (mapped.isSessionExpired) {
          saveRedirectTarget(location.pathname);
          navigate(`/auth/login?redirect=${encodeURIComponent(location.pathname)}`);
          return;
        }
        setError(mapped);
      })
      .finally(() => setLoading(false));
  }, [token, cocId, openUpgrade, location.pathname, navigate]);

  useEffect(() => {
    loadExam();
  }, [loadExam]);

  // Countdown timer for timed exam mode
  useEffect(() => {
    if (mode !== "playing" || !isTimed || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          handleSubmitExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [mode, isTimed, timeLeft]);

  if (loading) {
    return (
      <div className="py-24 flex justify-center text-muted-foreground text-xs sm:text-sm" aria-busy="true">
        <Loader2 className="animate-spin mr-2" size={16} /> Preparing exam…
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="max-w-md mx-auto py-16">
        <StatusMessage
          variant={error?.variant ?? "error"}
          title={error?.title ?? "Exam not found"}
          message={error?.message ?? "We could not open this exam. Please try again."}
          requestId={error?.requestId}
          action={{
            label: "Retry",
            onClick: loadExam,
          }}
        />
      </div>
    );
  }

  const questions = exam.questions || [];
  const currentQ = questions[currentIndex];
  const totalQ = questions.length;
  const isLockedFullExam = exam.access === "locked" || (!isPremium && !exam.is_free && exam.access !== "full");

  function handleSelectOption(optIdx: number) {
    if (mode === "results") return;
    const qid = currentQ?.id || `q_${currentIndex}`;
    setSelectedAnswers((prev) => ({ ...prev, [qid]: optIdx }));
  }

  function handleBackClick() {
    if (mode === "playing" && Object.keys(selectedAnswers).length > 0) {
      setShowExitConfirm(true);
    } else {
      location.key !== "default" ? navigate(-1) : navigate("/student/coc");
    }
  }

  async function handleSubmitExam() {
    if (submitting) return;
    setSubmitting(true);
    let submitted = false;
    try {
      if (token && cocId) {
        const scoreRes = await api.student.coc.submit(token, cocId, selectedAnswers);
        setServerScore(scoreRes);
        toast.success("Exam submitted.");
      }
      submitted = true;
    } catch (e) {
      const mapped = mapApiError(e);
      if (mapped.isPremiumRequired) {
        openUpgrade();
      } else {
        toast.error(mapped.message);
      }
    } finally {
      setSubmitting(false);
      if (submitted) setMode("results");
    }
  }

  // Local fallback calculation if needed
  function calculateLocalScore() {
    let correct = 0;
    let unanswered = 0;
    questions.forEach((q, idx) => {
      const qid = q.id || `q_${idx}`;
      const ans = selectedAnswers[qid];
      if (ans === undefined) {
        unanswered += 1;
      } else if (ans === q.correct_answer) {
        correct += 1;
      }
    });
    const wrong = totalQ - correct - unanswered;
    const percentage = totalQ > 0 ? Math.round((correct / totalQ) * 100) : 0;
    return {
      correct,
      wrong,
      unanswered,
      total: totalQ,
      percentage,
      passed: percentage >= (exam?.pass_percentage || 50),
    };
  }

  const localResult = calculateLocalScore();
  const passTarget = exam.pass_percentage || 50;
  const finalPercentage = serverScore ? serverScore.score_percentage : localResult.percentage;
  const isPassed = serverScore ? serverScore.passed : localResult.passed;

  const minutesRemaining = Math.floor(timeLeft / 60);
  const secondsRemaining = timeLeft % 60;
  const timerDisplay = `${minutesRemaining}:${secondsRemaining < 10 ? "0" : ""}${secondsRemaining}`;

  const currentPassageId = currentQ ? ((currentQ as any).passageId || (currentQ as any).passage_id) : null;
  const currentPassage = currentPassageId && exam?.passages ? exam.passages[currentPassageId] : null;

  return (
    <div className={`${currentPassage ? "max-w-6xl" : "max-w-3xl"} mx-auto space-y-6`}>
      {/* Confirm Leaving Running Exam Dialog */}
      <ConfirmDialog
        isOpen={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        onConfirm={() => location.key !== "default" ? navigate(-1) : navigate("/student/coc")}
        title="Leave exam?"
        message="Your answers have not been submitted yet. Are you sure you want to leave this session?"
        confirmLabel="Leave exam"
        variant="destructive"
      />

      {/* Header breadcrumb */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleBackClick}
          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground cursor-pointer"
        >
          <ChevronLeft size={15} /> Back to COC exams
        </button>
        <span className="px-2.5 py-0.5 rounded-xs text-xs font-semibold bg-primary/10 text-primary border border-primary/20">
          {exam.category}
        </span>
      </div>

      {/* ─── Screen 1: Timed Exam Entry Screen ─── */}
      {mode === "entry" && (
        <div className="space-y-6">
          <div className="bg-card rounded-lg border border-border p-5 sm:p-6 space-y-3">
            <h2 className="text-xl sm:text-2xl font-display font-bold text-foreground">
              {formatDisplayTitle(exam.title)}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              Pass target: {exam.pass_percentage}% · Total: {exam.question_count} questions · Time limit: {exam.time_limit_minutes || 60}m
            </p>
          </div>

          <div className="space-y-4">
            {/* Entry Option 1: Preview Set */}
            <div className="bg-card rounded-lg border border-border p-5 flex flex-col justify-between space-y-4">
              <div className="space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-primary">Free preview</span>
                <h3 className="text-base font-bold text-foreground">Preview questions</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Start with the first {questions.length} questions. Includes instant answer feedback and review.
                </p>
              </div>

              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={() => {
                  setIsTimed(false);
                  setMode("playing");
                }}
                icon={<Play size={13} />}
                className="w-full"
              >
                Start preview ({questions.length} questions)
              </Button>
            </div>

            {/* Entry Option 2: Full Timed Exam (Locked for Free Users) */}
            <PremiumGate isLocked={isLockedFullExam} buttonLabel="Get premium">
              <div className="bg-card rounded-lg border border-border p-5 flex flex-col justify-between space-y-4 h-full">
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Full simulation</span>
                  <h3 className="text-base font-bold text-foreground">Full timed exam</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Timed exam with {exam.question_count} questions, countdown timer, and comprehensive score breakdown.
                  </p>
                </div>

                <Button
                  type="button"
                  variant="primary"
                  size="md"
                  disabled={isLockedFullExam}
                  onClick={() => {
                    setIsTimed(true);
                    setMode("playing");
                  }}
                  icon={<Play size={13} />}
                  className="w-full"
                >
                  Start timed exam
                </Button>
              </div>
            </PremiumGate>
          </div>
        </div>
      )}

      {/* ─── Screen 2: Question Playing Screen ─── */}
      {mode === "playing" && (
        <div className="space-y-4">
          {/* Top Bar with Timer and Progress */}
          <div className="bg-card rounded-lg border border-border p-4 flex items-center justify-between gap-4">
            <div>
              <p className="font-bold text-xs sm:text-sm text-foreground line-clamp-1">{formatDisplayTitle(exam.title)}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Question {currentIndex + 1} of {totalQ}
              </p>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              {isTimed && (
                <div className="flex items-center gap-1 text-xs font-mono font-bold text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-xs">
                  <Clock size={13} /> {timerDisplay}
                </div>
              )}
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleSubmitExam}
                loading={submitting}
                loadingText="Submitting…"
              >
                Submit exam
              </Button>
            </div>
          </div>

          {/* Question Card */}
          {currentQ && (
            <QuestionPassageLayout passage={currentPassage}>
              <div className="bg-card rounded-sm border border-border p-5 sm:p-6 space-y-5">
                <h3 className="text-sm sm:text-base font-bold text-foreground leading-relaxed">
                  <span className="text-primary mr-2">Q{currentIndex + 1}.</span>
                  <MathText inline>{currentQ.text}</MathText>
                </h3>

                {/* Question Figure */}
                {currentQ.figure_image && (
                  <QuestionFigure
                    figureImage={currentQ.figure_image}
                    figureAlt={currentQ.figure_alt}
                    figureCaption={currentQ.figure_alt}
                  />
                )}

                {/* Options */}
                <div className="space-y-2.5">
                  {currentQ.options.map((opt, optIdx) => {
                    const qid = currentQ.id || `q_${currentIndex}`;
                    const isSelected = selectedAnswers[qid] === optIdx;

                    return (
                      <button
                        key={optIdx}
                        type="button"
                        onClick={() => handleSelectOption(optIdx)}
                        className={`w-full text-left p-3.5 rounded-xs border transition flex items-center gap-3 text-xs sm:text-sm ${
                          isSelected
                            ? "border-primary bg-primary/10 font-semibold text-foreground"
                            : "border-border bg-card hover:bg-muted text-foreground"
                        }`}
                      >
                        <span
                          className={`w-6 h-6 rounded-xs text-xs font-bold flex items-center justify-center shrink-0 border ${
                            isSelected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-muted text-muted-foreground border-border"
                          }`}
                        >
                          {String.fromCharCode(65 + optIdx)}
                        </span>
                        <span className="flex-1">
                          <MathText inline>{opt}</MathText>
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Bottom Navigation */}
                <div className="flex items-center justify-between pt-4 border-t border-border text-xs">
                  <button
                    type="button"
                    disabled={currentIndex === 0}
                    onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
                    className="px-3.5 py-1.5 rounded-xs border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 font-semibold"
                  >
                    Previous
                  </button>

                  {currentIndex < totalQ - 1 ? (
                    <button
                      type="button"
                      onClick={() => setCurrentIndex((prev) => Math.min(totalQ - 1, prev + 1))}
                      className="btn btn-primary px-4 py-1.5 rounded-xs font-semibold"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSubmitExam}
                      disabled={submitting}
                      className="btn btn-primary px-4 py-1.5 rounded-xs font-semibold"
                    >
                      Submit exam
                    </button>
                  )}
                </div>
              </div>
            </QuestionPassageLayout>
          )}
        </div>
      )}

      {/* ─── Screen 3: Exam Results Screen ─── */}
      {mode === "results" && (
        <div className="space-y-6">
          <div className="bg-card rounded-sm border border-border p-5 sm:p-6 text-center space-y-2">
            <h2 className="text-xl sm:text-2xl font-display font-bold text-foreground">Exam complete</h2>
            <p className="text-xs text-muted-foreground">{formatDisplayTitle(exam.title)}</p>
            <div className="pt-2">
              <span
                className={`text-xs font-bold px-3 py-1 rounded-xs inline-block ${
                  isPassed
                    ? "bg-success/10 text-success border border-success/30"
                    : "bg-warning/10 text-warning border border-warning/30"
                }`}
              >
                {isPassed ? "Passed" : "Did not pass"} · {finalPercentage}% (pass mark: {passTarget}%)
              </span>
            </div>
          </div>

          {/* Question Review List */}
          <div className="space-y-3">
            <h3 className="font-bold text-sm text-foreground">Review answers</h3>
            {questions.map((q, idx) => {
              const qid = q.id || `q_${idx}`;
              const userAns = selectedAnswers[qid];
              const isCorrect = userAns === q.correct_answer;
              const pId = (q as any).passageId || (q as any).passage_id;
              const passageMap = serverScore?.passages || exam.passages;
              const passage = pId && passageMap ? passageMap[pId] : null;
              const prevPId = idx > 0 ? ((questions[idx - 1] as any).passageId || (questions[idx - 1] as any).passage_id) : null;
              const isFirstInPassage = Boolean(pId && pId !== prevPId);

              return (
                <QuestionPassageLayout
                  key={qid}
                  passage={isFirstInPassage ? passage : null}
                  variant="multi"
                >
                  <div className="p-4 rounded-sm border border-border bg-card space-y-3 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold text-foreground">Question {idx + 1}</span>
                      <span
                        className={`text-xs font-bold px-2 py-0.5 rounded-xs ${
                          userAns === undefined
                            ? "bg-muted text-muted-foreground"
                            : isCorrect
                            ? "bg-success/10 text-success border border-success/30"
                            : "bg-destructive/10 text-destructive border border-destructive/30"
                        }`}
                      >
                        {userAns === undefined ? "Not answered" : isCorrect ? "Correct" : "Incorrect"}
                      </span>
                    </div>

                    <p className="text-foreground leading-relaxed">
                      <MathText inline>{q.text}</MathText>
                    </p>

                    {q.figure_image && (
                      <QuestionFigure
                        image={q.figure_image}
                        alt={q.figure_alt}
                        location={q.figure_location}
                        className="my-3"
                      />
                    )}

                    <div className="space-y-1 text-muted-foreground">
                      <p>
                        Correct answer:{" "}
                        <span className="font-semibold text-foreground">
                          {String.fromCharCode(65 + q.correct_answer)}. {q.options[q.correct_answer]}
                        </span>
                      </p>
                      {userAns !== undefined && !isCorrect && (
                        <p>
                          Your answer:{" "}
                          <span className="font-semibold text-destructive">
                            {q.options[userAns] === undefined ? "Your answer unavailable" : `${String.fromCharCode(65 + userAns)}. ${q.options[userAns]}`}
                          </span>
                        </p>
                      )}
                    </div>

                    {q.explanation && (
                      <div className="p-2.5 rounded-xs bg-muted/60 text-muted-foreground border border-border">
                        <span className="font-bold text-foreground block mb-0.5">Explanation</span>
                        <MathText inline>{q.explanation}</MathText>
                      </div>
                    )}
                  </div>
                </QuestionPassageLayout>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setSelectedAnswers({});
                setCurrentIndex(0);
                setMode("entry");
              }}
              className="btn btn-primary flex-1 py-2 rounded-xs text-xs font-semibold inline-flex items-center justify-center gap-1.5"
            >
              <RotateCcw size={13} /> Retake exam
            </button>
            <button
              type="button"
              onClick={() => location.key !== "default" ? navigate(-1) : navigate("/student/coc")}
              className="px-4 py-2 rounded-xs border border-border text-xs font-semibold hover:bg-muted"
            >
              Back to COC exams
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
